import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

// lazyWithRetry — wrappt React.lazy mit auto-retry-once gegen transiente
// chunk-load-failures (Netzwerk-Hiccup, kurzer CDN-Aussetzer, post-deploy
// stale-asset-hash race). Bei zwei aufeinanderfolgenden failures wirft der
// Wrapper den ERSTEN Error (root-cause), nicht den zweiten (cascade-noise);
// der LazyChunkErrorBoundary fängt das wie bisher ab.
//
// Trade-off: User wartet bei Fehlerfall ~1.5s extra vor der Suspense-fallback
// für den retry-Versuch. Für die ~95% transient-failures ist das ein guter
// Deal (no reload, page-state preserved) gegen die ~5% persistent failures
// (eh full reload nötig).
//
// Trifft NICHT die "True in-place Retry"-Variante (versioned-identity
// Context, in-place retry-Button im Panel) — die ist als separater Sprint
// dokumentiert in memory/todo.md, nur wenn explizit UX-Anforderung besteht.

const RETRY_DELAY_MS = 1500;

// Generic constraint mirrors React.lazy: `T extends ComponentType<any>` lets
// TS infer the consumer's concrete props (e.g. `{ id: string }` on detail pages).
// Tightening to `ComponentType<unknown>` breaks inference (consumer-side props
// resolve to `IntrinsicAttributes` only).
type ModuleDefault<T> = { default: T };
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy signature
type Factory<T extends ComponentType<any>> = () => Promise<ModuleDefault<T>>;

// Exported separately for direct unit-testing without React/Suspense involvement.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy signature
export async function retryFactory<T extends ComponentType<any>>(
  factory: Factory<T>,
  delayMs: number = RETRY_DELAY_MS,
): Promise<ModuleDefault<T>> {
  try {
    return await factory();
  } catch (firstError) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    try {
      return await factory();
    } catch {
      // Throw the FIRST error — it's the root cause. Second error after
      // retry is often cascade-noise (half-warm state, different symptom).
      throw firstError;
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy signature
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: Factory<T>,
): LazyExoticComponent<T> {
  return lazy(() => retryFactory(factory));
}
